import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Badge } from '@mui/icons-material';

const roleOptions = [
  'Manager',
  'Receptionist',
  'Housekeeping',
  'Chef',
  'Security',
  'Maintenance',
  'Accountant',
  'Waiter',
];

const departmentOptions = [
  'Management',
  'Front Desk',
  'Housekeeping',
  'Kitchen',
  'Security',
  'Maintenance',
  'Finance',
  'Food & Beverage',
];

const statusOptions = ['Active', 'On Leave', 'Inactive'];
const shiftOptions = ['Day', 'Night', 'Rotating'];

export default function ManageStaffDialog({ open, onClose, staff, onSave, mode = 'add' }) {
  const [formData, setFormData] = useState({
    name: '',
    role: 'Receptionist',
    department: 'Front Desk',
    email: '',
    phone: '',
    salary: '',
    status: 'Active',
    shift: 'Day',
    joinDate: new Date().toISOString().split('T')[0],
    address: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (staff && mode === 'edit') {
      setFormData({
        ...staff,
      });
    } else if (mode === 'add') {
      setFormData({
        name: '',
        role: 'Receptionist',
        department: 'Front Desk',
        email: '',
        phone: '',
        salary: '',
        status: 'Active',
        shift: 'Day',
        joinDate: new Date().toISOString().split('T')[0],
        address: '',
      });
    }
  }, [staff, mode]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }
    if (!formData.salary || parseFloat(formData.salary) <= 0) {
      newErrors.salary = 'Valid salary is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const staffData = {
      ...formData,
      salary: parseFloat(formData.salary),
      id: staff?.id || `S${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
    };

    onSave(staffData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      role: 'Receptionist',
      department: 'Front Desk',
      email: '',
      phone: '',
      salary: '',
      status: 'Active',
      shift: 'Day',
      joinDate: new Date().toISOString().split('T')[0],
      address: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Badge color="primary" />
            <Typography variant="h6">
              {mode === 'add' ? 'Add New Staff Member' : 'Edit Staff Member'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Join Date"
              type="date"
              value={formData.joinDate}
              onChange={(e) => handleChange('joinDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Employment Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Role"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Department"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
            >
              {departmentOptions.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Monthly Salary"
              type="number"
              value={formData.salary}
              onChange={(e) => handleChange('salary', e.target.value)}
              error={!!errors.salary}
              helperText={errors.salary}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Shift"
              value={formData.shift}
              onChange={(e) => handleChange('shift', e.target.value)}
            >
              {shiftOptions.map((shift) => (
                <MenuItem key={shift} value={shift}>
                  {shift}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {mode === 'add' ? 'Add Staff Member' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
